/** @format */

import React, { createContext, useEffect, useState } from 'react'
import styled from 'styled-components'
import { _$, Card, Button, Table, TableColumn, Pagination, Icon } from '@king-design/react'
import { TableSortValue } from '@king-design/react'
import MonitorCrumb from 'src/components/MonitorCrumb'
import { MonitorSearch, UserInfo } from 'src/components'
import { useHistory, useLocation } from 'react-router-dom'
import { TriggerRecordItemProp, ActionTypeEnum, TriggerTypeEnum, getStatus } from './container'
import dayjs from 'dayjs'
import RuleDrawer from './components/RuleDrawer/index'
import VersionDrawer from './components/VersionDrawer'
import OptLogDrawer from './components/OptLogDrawer'
import DeleteDialog from './components/DeleteDialog'
import EnableDialog from './components/EnableDialog'
import { getTriggerList } from 'src/api/ApiEventOpsTrigger'
import { getSceneList } from 'src/api/ApiEventOpsScene'
import { useAsync, useAsyncFn } from 'react-use'
import { SceneRecordItemProp } from '../Scene/container'
import { request } from 'src/utils'
import API from 'src/api/ApiContacts'
import APIEventCenter from 'src/api/ApiEventCenter'
import { EventItemProps } from 'src/pages/EventCenter/constants'
import { UserItemProps, GroupItemProps } from 'src/components/AlarmReciver/new'

interface SearchProps {
  Page: number
  PageSize: number
  RuleName: string
}
const initSearch: SearchProps = {
  Page: 1,
  PageSize: 10,
  RuleName: ''
}

// 抽屉基础信息
export interface DrawerProps {
  visible: boolean
  record: TriggerRecordItemProp
}
export const initDrawer: DrawerProps = {
  visible: false,
  record: undefined
}
// 规则抽屉
export interface RuleDrawerProps extends DrawerProps {
  editable: boolean
  openMethod?: 'sign' | 'ID'
}
const initRuleDrawer: RuleDrawerProps = {
  visible: false,
  editable: false,
  record: undefined
}

export interface IContextType {
  sceneList?: SceneRecordItemProp[]
  fetchSceneList?: () => void
  reciverResult?: {
    userlist?: UserItemProps[]
    grouplist?: GroupItemProps[]
  }
  eventResult?: EventItemProps[]
}
function useQuery() {
  return new URLSearchParams(useLocation().search)
}

export const CountContext = createContext<IContextType>({})
const TriggerPage: React.FC<{ className?: string }> = ({ className }) => {
  const history = useHistory()
  const query = useQuery()
  const actionSign = query.get('actionSign') || ''
  const initName = query.get('name') || ''
  const [search, setSearch] = useState<SearchProps>({
    ...initSearch,
    RuleName: initName ? decodeURIComponent(initName) : ''
  })
  const [sort, setSort] = useState<TableSortValue>({})
  // 编辑规则/新建规则抽屉
  const [drawer, setDrawer] = useState<RuleDrawerProps>(initRuleDrawer)
  // 版本抽屉
  const [versionDrawer, setVersionDrawer] = useState<DrawerProps>(initDrawer)
  // 操作日志抽屉
  const [optLogDrawer, setOptLogDrawer] = useState<DrawerProps>(initDrawer)
  // 删除规则二次确认
  const [deleteDialog, setDeleteDialog] = useState<DrawerProps>(initDrawer)
  // 启用/停用备注
  const [enableDialog, setEnableDialog] = useState<DrawerProps>(initDrawer)

  // 获取触发器列表
  const [{ value: triggerList }, fetchTriggerList] = useAsyncFn(
    async (params?) => {
      const result = await getTriggerList({
        ...params,
        ActionSign: actionSign,
        Status: actionSign ? 2 : undefined,
        Offset: (search.Page - 1) * search.PageSize,
        Limit: search.PageSize,
        Search: search.RuleName,
        Order: sort?.key && sort?.type ? `${sort?.key} ${sort?.type}` : undefined
      })
      return result
    },
    [search, sort, actionSign]
  )

  // 联系人/联系组
  const { value: reciverResult } = useAsync(async () => {
    const userResult = await request(API.getAlertUser, {
      method: 'get',
      data: {
        PageIndex: 1,
        Cap: 1
      }
    })

    const groupResult = await request(API.getUserGroup, {
      method: 'get',
      data: {
        PageIndex: 1,
        Cap: 1
      }
    })
    return {
      userlist: userResult.data?.userlist || [],
      grouplist: groupResult.data?.usergrplist || []
    }
  }, [])

  // 事件
  const { value: eventResult } = useAsync(async () => {
    const result = await request(APIEventCenter.getListSysEvents, {
      data: {
        ProductType: 999
      }
    })
    if (result && result?.code === 'Success' && result?.data.eventList) {
      return result?.data.eventList
    }
  }, [])

  // 获取自动化场景列表
  const [{ value: sceneList }, fetchSceneList] = useAsyncFn(async () => {
    const result = await getSceneList({ Limit: 999 })
    return result?.Info || []
  }, [])

  useEffect(() => {
    fetchTriggerList()
  }, [search, sort])

  useEffect(() => {
    fetchSceneList()
  }, [])

  return (
    <CountContext.Provider
      value={{
        sceneList: sceneList || [],
        fetchSceneList,
        reciverResult,
        eventResult
      }}
    >
      <div className={className}>
        <MonitorCrumb breadArr={[{ title: _$('自动化运维') }, { title: _$('触发器') }]} />
        <Card style={{ marginBottom: 16 }}>
          <div className="search_row">
            <div className="ksc-space">
              <div className="search_item">
                <p className="search_item_label">{_$('规则名称')}</p>
                <MonitorSearch
                  placeholder={_$('请输入规则名称，支持模糊搜索')}
                  value={search.RuleName}
                  onChange={value => {
                    setSearch({
                      ...search,
                      Page: 1,
                      RuleName: value
                    })
                  }}
                />
              </div>
            </div>
            <div className="ksc-space">
              <Button
                type="primary"
                onClick={() => {
                  setDrawer({
                    visible: true,
                    editable: true,
                    record: undefined
                  })
                }}
              >
                <Icon className="ion-plus" />
                {_$('新增规则')}
              </Button>
              <Button
                icon
                onClick={() => {
                  fetchTriggerList()
                }}
              >
                <Icon className="ion-refresh" />
              </Button>
            </div>
          </div>
          <Table
            data={triggerList?.Info || []}
            sort={sort}
            onChangeSort={sort => {
              setSearch({
                ...search,
                Page: 1
              })
              setSort(sort)
            }}
            animation={false}
            rowKey={record => record.id}
            checkType="none"
          >
            <TableColumn key="ID" title="ID" sortable width="80" />
            <TableColumn key="Name" title={_$('规则名称')} sortable width="200" />
            <TableColumn
              key="TriggerType"
              title={_$('触发源')}
              sortable
              width="120"
              slotTemplate={([{ TriggerType }]) => {
                return TriggerTypeEnum[TriggerType] || '-'
              }}
            />
            <TableColumn
              key="ActionType"
              title={_$('触发动作')}
              sortable
              width="120"
              slotTemplate={([{ ActionType }]) => {
                return ActionTypeEnum[ActionType] || '-'
              }}
            />
            <TableColumn
              key="Creator"
              title={_$('操作人')}
              sortable
              width="100"
              slotTemplate={([{ Creator }]) => {
                return <UserInfo name={Creator} />
              }}
            />
            <TableColumn
              key="PublishedAt"
              title={_$('发布时间')}
              sortable
              width="100"
              slotTemplate={([{ PublishedAt }]) => {
                return PublishedAt ? dayjs(PublishedAt).format('YYYY-MM-DD HH:mm:ss') : '-'
              }}
            />
            <TableColumn
              key="Version"
              title={_$('版本')}
              width="80"
              slotTemplate={([{ Version }]) => {
                return Version || '-'
              }}
            />
            <TableColumn
              key="TriggerTimes"
              title={_$('触发记录/次')}
              width="100"
              slotTemplate={([{ Sign, TriggerTimes }]) => {
                return TriggerTimes > 0 ? (
                  <a onClick={() => history.push(`/monitor/eventops/trigger/record/${Sign}`)}>
                    {TriggerTimes}
                  </a>
                ) : (
                  <span>0</span>
                )
              }}
            />
            <TableColumn
              key="Status"
              title={_$('状态')}
              fixed="right"
              width="100"
              sortable
              slotTemplate={([{ Status }]) => {
                return getStatus(Status)
              }}
            />
            <TableColumn
              key="operations"
              title={_$('操作')}
              fixed="right"
              width="200"
              slotTemplate={([record]) => {
                return (
                  <div className="ksc-space">
                    {/* 查看/编辑 */}
                    <a
                      onClick={() => {
                        setDrawer({
                          visible: true,
                          editable: false,
                          record
                        })
                      }}
                    >
                      {_$('查看')}
                    </a>
                    {/* 启用/停用 */}
                    {record.Status === 2 || record.Status === -1 ? (
                      <a
                        onClick={() => {
                          setEnableDialog({
                            visible: true,
                            record
                          })
                        }}
                      >
                        {record.Status === 2 ? _$('停用') : _$('启用')}
                      </a>
                    ) : (
                      <a className="disabled-text">{_$('停用')}</a>
                    )}
                    {/* 版本 */}
                    <a
                      onClick={() => {
                        setVersionDrawer({
                          visible: true,
                          record
                        })
                      }}
                    >
                      {_$('版本')}
                    </a>
                    {/* 删除 */}
                    <a
                      onClick={() => {
                        setDeleteDialog({
                          visible: true,
                          record
                        })
                      }}
                    >
                      {_$('删除')}
                    </a>
                    {/* 操作日志 */}
                    <a
                      onClick={() => {
                        setOptLogDrawer({
                          visible: true,
                          record
                        })
                      }}
                    >
                      {_$('操作日志')}
                    </a>
                  </div>
                )
              }}
            />
          </Table>
          <Pagination
            value={search.Page}
            limit={search.PageSize}
            size="small"
            total={triggerList?.Total || 0}
            onChangeValue={value => {
              setSearch({
                ...search,
                Page: value
              })
            }}
            onChangeLimit={limit => {
              setSearch({
                ...search,
                PageSize: limit
              })
            }}
          />
        </Card>
        {/* 规则抽屉 */}
        <RuleDrawer
          {...drawer}
          onEdit={editable => {
            setDrawer({ ...drawer, editable })
          }}
          onClose={(update?) => {
            setDrawer(initRuleDrawer)
            if (update) {
              fetchTriggerList()
            }
          }}
        />
        {/* 版本抽屉 */}
        <VersionDrawer
          {...versionDrawer}
          onClose={() => {
            setVersionDrawer(initDrawer)
          }}
          openEdit={(record, editable) => {
            setDrawer({
              visible: true,
              editable: editable,
              record: record,
              openMethod: 'ID'
            })
          }}
        />
        {/* 操作日志抽屉 */}
        <OptLogDrawer
          {...optLogDrawer}
          onClose={() => {
            setOptLogDrawer(initDrawer)
          }}
        />
        {/* 删除二次确认弹窗 */}
        <DeleteDialog
          {...deleteDialog}
          onClose={update => {
            setDeleteDialog(initDrawer)
            if (update) {
              setSearch({
                ...search,
                Page: 1
              })
            }
          }}
        />
        {/* 启用/停用备注弹窗 */}
        <EnableDialog
          {...enableDialog}
          onClose={update => {
            setEnableDialog(initDrawer)
            if (update) {
              fetchTriggerList()
            }
          }}
        />
      </div>
    </CountContext.Provider>
  )
}

export default styled(TriggerPage)`
  .search_row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 16px;
    .search_item {
      display: flex;
      align-items: center;
      .search_item_label {
        margin: 0px 8px 0 0;
      }
    }
  }
  .k-pagination {
    margin-top: 16px;
    text-align: center;
  }
`
